import React, { useState, useEffect, useRef } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

interface FocusRetainingTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  validationPattern?: RegExp;
}

/**
 * A special TextField that maintains focus during typing and state updates
 * This component maintains its own internal state to prevent focus loss
 */
const FocusRetainingTextField: React.FC<FocusRetainingTextFieldProps> = ({
  value,
  onChange,
  validationPattern,
  ...props
}) => {
  // Local state that will be updated instantly
  const [localValue, setLocalValue] = useState(value || '');
  
  // Reference to the input element
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Track if this field is actively being used
  const wasActiveRef = useRef<boolean>(false);
  
  // Cursor position tracking
  const cursorPositionRef = useRef<number | null>(null);
  
  // Last interaction time to detect if blur should be prevented
  const lastInteractionTimeRef = useRef<number>(0);
  
  // Update our local value when the external value changes (and we're not focused)
  useEffect(() => {
    // Only update if not currently editing (to prevent focus loss)
    if (document.activeElement !== inputRef.current) {
      setLocalValue(value || '');
    }
  }, [value]);
  
  // Handle local changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Store current cursor position
    cursorPositionRef.current = e.target.selectionStart;
    
    // Update last interaction time
    lastInteractionTimeRef.current = Date.now();
    
    // If there's a validation pattern, check the value
    if (validationPattern) {
      if (newValue === '' || validationPattern.test(newValue)) {
        setLocalValue(newValue);
        onChange(newValue);
      }
    } else {
      // No validation needed
      setLocalValue(newValue);
      onChange(newValue);
    }
  };
  
  // Handle focus
  const handleFocus = () => {
    wasActiveRef.current = true;
    lastInteractionTimeRef.current = Date.now();
  };
  
  // Handle blur - prevent only if it appears to be unintentional
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Only prevent blur if it happens right after typing (within 300ms)
    const timeSinceLastInteraction = Date.now() - lastInteractionTimeRef.current;
    
    // If this blur is happening very shortly after typing and not to another input, 
    // it's likely unintentional
    if (timeSinceLastInteraction < 300 && !e.relatedTarget) {
      e.preventDefault();
      
      // Force focus back to this input on next tick
      setTimeout(() => {
        if (inputRef.current && wasActiveRef.current) {
          inputRef.current.focus();
          
          // Restore cursor position
          if (cursorPositionRef.current !== null) {
            try {
              inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
            } catch (e) {
              // Ignore selection errors
            }
          }
        }
      }, 0);
      
      return false;
    }
    
    // If blur is happening after a reasonable time or to a specific target, allow it
    wasActiveRef.current = false;
  };
  
  // Handle keydown to update cursor and interaction time
  const handleKeyDown = () => {
    if (inputRef.current) {
      cursorPositionRef.current = inputRef.current.selectionStart;
      lastInteractionTimeRef.current = Date.now();
    }
  };
  
  // Restore cursor position after render
  useEffect(() => {
    if (inputRef.current && document.activeElement === inputRef.current && cursorPositionRef.current !== null) {
      inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
    }
  });
  
  return (
    <TextField
      {...props}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      inputRef={inputRef}
      autoComplete="off"
    />
  );
};

export default FocusRetainingTextField; 